import { useApiRequest } from '@/shared/api/useApiRequest'
import apiClient from '@/api/client'
import type { AxiosResponse } from 'axios'

export interface SupplierProduct {
  id: number
  name: string
  sku: string
  description: string
  price: number
  stock: number
  status: 'active' | 'inactive'
  category: string
  unit: string
  image: string
  created_at: string
  updated_at: string
}

export interface SupplierProductFilters {
  page?: number
  limit?: number
  category?: string
  status?: string
  search?: string
  [key: string]: string | number | boolean | string[] | undefined
}

export interface SupplierProductStatistics {
  total_products: number
  active_products: number
  low_stock_products: number
  out_of_stock_products: number
  categories: Array<{
    name: string
    count: number
  }>
  top_products: SupplierProduct[]
}

export interface CreateSupplierProductData {
  name: string
  sku: string
  description?: string
  price: number
  stock: number
  category: string
  unit: string
  active: boolean
  images?: File[]
  supplier_address_id?: number | null
}

export interface UpdateSupplierProductData extends Partial<CreateSupplierProductData> {
  id: number
}

export const supplierProductApi = {
  /**
   * Получить список товаров поставщика
   */
  async getProducts(filters: SupplierProductFilters = {}) {
    const { makeRequest } = useApiRequest()
    const cleanFilters = Object.fromEntries(
      Object.entries(filters).filter(([_, v]) => v !== undefined),
    ) as Record<string, string | number | boolean | string[]>
    return await makeRequest('/api/supplier/products', { method: 'GET' }, cleanFilters)
  },

  /**
   * Получить товар по ID
   */
  async getProduct(id: number) {
    const { makeRequest } = useApiRequest()
    return await makeRequest(`/api/supplier/products/${id}`)
  },

  /**
   * Создать товар
   */
  async createProduct(data: CreateSupplierProductData) {
    const { makeRequest } = useApiRequest()
    return await makeRequest('/api/supplier/products', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  /**
   * Обновить товар
   */
  async updateProduct(id: number, data: Partial<CreateSupplierProductData>) {
    const { makeRequest } = useApiRequest()
    return await makeRequest(`/api/supplier/products/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  },

  /**
   * Удалить товар
   */
  async deleteProduct(id: number) {
    const { makeRequest } = useApiRequest()
    return await makeRequest(`/api/supplier/products/${id}`, { method: 'DELETE' })
  },

  /**
   * Получить статистику товаров
   */
  async getStatistics() {
    const { makeRequest } = useApiRequest()
    return await makeRequest('/api/supplier/products/statistics')
  },

  /**
   * Массовое обновление товаров
   */
  async bulkUpdate(products: UpdateSupplierProductData[]) {
    const { makeRequest } = useApiRequest()
    return await makeRequest('/api/supplier/products/bulk', {
      method: 'PUT',
      body: JSON.stringify({ products }),
    })
  },

  /**
   * Импорт товаров из файла
   */
  async importProducts(file: File) {
    const { makeRequest } = useApiRequest()
    const formData = new FormData()
    formData.append('file', file)

    return await makeRequest('/api/supplier/products/import', {
      method: 'POST',
      headers: {},
      body: formData,
    })
  },

  /**
   * Экспорт товаров
   */
  async exportProducts(filters: { category?: string; status?: string } = {}) {
    const { makeRequest } = useApiRequest()
    return await makeRequest('/api/supplier/products/export', { method: 'GET' }, filters)
  },
}

/**
 * Товар в форме редактирования на SupplierProducts.vue.
 * Отличается от SupplierProduct (см. выше): используется напрямую компонентом
 * до отправки на бэкенд, включает File[] для ещё не загруженных изображений.
 */
export interface SupplierProductFormItem {
  id?: number
  name: string
  sku: string
  category: string
  unit: string
  price: number
  stock: number
  status?: string
  description: string
  image?: string
  images?: File[]
  active?: boolean
  supplier_address_id?: number | string | null
  supplier_address_label?: string | null
}

/**
 * Raw axios passthrough - только для endpoint'ов, несовместимых с useApiRequest
 * (export отдаёт бинарный файл, не {success, data}-конверт, поэтому makeRequest
 * не подходит принципиально, не просто по стилю). Остальные операции над товаром
 * (list/create/update/delete) - используй supplierProductApi выше.
 */
export const supplierProductCatalogApi = {
  async export(filters: { category?: string; status?: string }): Promise<AxiosResponse> {
    return apiClient.get('/api/supplier/products/export', {
      params: filters,
      responseType: 'blob',
    })
  },
}
